import React, { useMemo, useState } from "react";
import styled from "styled-components";
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Tooltip,
  CircularProgress,
} from "@mui/material";
import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ArrowRightIcon from "@mui/icons-material/ArrowRight";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import toast from "react-hot-toast";
import {
  useGetHierarchyQuery,
  useDeleteSingleStaffMutation,
} from "../../../features/employeeSlice";
import { colors } from "../../../Shared/colors";
import { Card, CardHead, Empty } from "../staffStyles";
import TeamTreeGraph from "./TeamTreeGraph";

/**
 * The team structure — one dataset, one card, two ways to read it.
 *
 * The chart and the table were separate cards showing exactly the same data,
 * so the page repeated itself and neither view got the full width. They are
 * now one card with a toggle: the chart for shape, the list for detail.
 *
 * The list is a directory rather than a grid — dense rows, aligned columns,
 * and MANAGER spelled out, which is the column people actually came to read
 * and which the old view made you infer from indentation.
 */
export const TeamStructure = () => {
  const { data: hierarchy, isFetching } = useGetHierarchyQuery();
  const [deleteStaff, { isLoading: isDeleting }] = useDeleteSingleStaffMutation();

  const [view, setView] = useState("list");
  const [expanded, setExpanded] = useState({});
  const [pendingDelete, setPendingDelete] = useState(null);

  // The endpoint returns one root object with nested `children`, not an array —
  // reading `.length` on it is how an earlier version hid this whole card.
  const hasStructure = Boolean(hierarchy && (hierarchy.id || hierarchy.name));

  /**
   * Flattens the tree into visible rows, carrying each person's manager down
   * with them so the column can show a name instead of making the reader count
   * indents.
   */
  const rows = useMemo(() => {
    if (!hasStructure) return [];
    const out = [];

    const walk = (node, level, parentId, managerName) => {
      if (!node?.id) return;
      // "Ahmed Raza (bda)" arrives as one string; split so the role can be its
      // own column rather than a lowercase parenthetical inside the name.
      const match = /^(.*?)\s*\(([^)]+)\)\s*$/.exec(node.name || "");
      const children = node.children || [];
      const name = match ? match[1] : node.name;

      out.push({
        id: node.id,
        parentId,
        name,
        role: match ? match[2] : "",
        image: node.image,
        level,
        manager: managerName,
        teamSize: children.length,
        hasChildren: children.length > 0,
      });

      if (expanded[node.id]) {
        children.forEach((child) => walk(child, level + 1, node.id, name));
      }
    };

    walk(hierarchy, 0, null, null);
    return out;
  }, [hierarchy, expanded, hasStructure]);

  const totalPeople = useMemo(() => {
    const count = (node) =>
      !node ? 0 : 1 + (node.children || []).reduce((n, c) => n + count(c), 0);
    return hasStructure ? count(hierarchy) : 0;
  }, [hierarchy, hasStructure]);

  const toggle = (id) => setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));

  const expandAll = () => {
    const all = {};
    const walk = (node) => {
      if (!node?.id) return;
      if (node.children?.length) {
        all[node.id] = true;
        node.children.forEach(walk);
      }
    };
    walk(hierarchy);
    setExpanded(all);
  };

  const handleDelete = () => {
    if (!pendingDelete) return;
    deleteStaff({ staffId: pendingDelete.id, parentId: pendingDelete.parentId })
      .unwrap()
      .then(() => {
        toast.success(`${pendingDelete.name} removed from the structure`);
        setPendingDelete(null);
      })
      .catch((err) => {
        toast.error(
          err?.data?.message || err?.data?.error?.message || "Couldn't remove them"
        );
        setPendingDelete(null);
      });
  };

  const allExpanded =
    rows.length > 0 && rows.every((r) => !r.hasChildren || expanded[r.id]);

  return (
    <>
      <Card>
        <CardHead>
          <span className="count">
            Team structure
            {totalPeople > 0 ? (
              <>
                {" — "}
                <strong>{totalPeople}</strong>{" "}
                {totalPeople === 1 ? "person" : "people"}
              </>
            ) : null}
          </span>

          <HeadRight>
            {view === "list" && hasStructure ? (
              <TextAction
                type="button"
                onClick={() => (allExpanded ? setExpanded({}) : expandAll())}
              >
                {allExpanded ? "Collapse all" : "Expand all"}
              </TextAction>
            ) : null}

            <Segmented role="group" aria-label="Choose how to view the structure">
              <Seg
                type="button"
                $active={view === "list"}
                aria-pressed={view === "list"}
                onClick={() => setView("list")}
              >
                <FormatListBulletedIcon />
                List
              </Seg>
              <Seg
                type="button"
                $active={view === "chart"}
                aria-pressed={view === "chart"}
                onClick={() => setView("chart")}
              >
                <AccountTreeOutlinedIcon />
                Chart
              </Seg>
            </Segmented>
          </HeadRight>
        </CardHead>

        {isFetching ? (
          <Loading>
            <CircularProgress size={28} sx={{ color: colors.brown }} />
          </Loading>
        ) : !hasStructure ? (
          <Empty>
            <AccountTreeOutlinedIcon />
            <h3>No structure yet</h3>
            <p>
              Add someone above with a manager or team members, and they will
              appear here.
            </p>
          </Empty>
        ) : view === "chart" ? (
          <ChartArea>
            <TeamTreeGraph data={hierarchy} />
          </ChartArea>
        ) : (
          <TableScroll>
            <Table>
              <thead>
                <tr>
                  <th scope="col">Name</th>
                  <th scope="col">Role</th>
                  <th scope="col" className="num">Team</th>
                  <th scope="col">Reports to</th>
                  <th scope="col" className="actions">
                    <span className="sr">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.id}>
                    <td>
                      <NameCell $level={row.level}>
                        {row.hasChildren ? (
                          <Toggle
                            type="button"
                            aria-expanded={Boolean(expanded[row.id])}
                            aria-label={`${expanded[row.id] ? "Collapse" : "Expand"} ${row.name}'s team`}
                            onClick={() => toggle(row.id)}
                          >
                            {expanded[row.id] ? <ArrowDropDownIcon /> : <ArrowRightIcon />}
                          </Toggle>
                        ) : (
                          <Spacer aria-hidden="true" />
                        )}
                        <Initial aria-hidden="true">
                          {row.name?.trim()?.charAt(0)?.toUpperCase() || "?"}
                        </Initial>
                        <span className="who" title={row.name}>{row.name}</span>
                      </NameCell>
                    </td>
                    <td>
                      {row.role ? <Role>{row.role}</Role> : <Dash>—</Dash>}
                    </td>
                    <td className="num">
                      {row.teamSize > 0 ? row.teamSize : <Dash>—</Dash>}
                    </td>
                    <td>
                      {row.manager ? (
                        <span className="mgr">{row.manager}</span>
                      ) : (
                        <Dash>—</Dash>
                      )}
                    </td>
                    <td className="actions">
                      <Tooltip title={`Remove ${row.name} from the structure`}>
                        <RowAction
                          type="button"
                          aria-label={`Remove ${row.name} from the team structure`}
                          onClick={() => setPendingDelete(row)}
                        >
                          <DeleteOutlineIcon />
                        </RowAction>
                      </Tooltip>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </TableScroll>
        )}
      </Card>

      <Dialog
        open={Boolean(pendingDelete)}
        onClose={() => setPendingDelete(null)}
        PaperProps={{ sx: { borderRadius: "14px", maxWidth: 420 } }}
      >
        <DialogTitle sx={{ fontWeight: 700, fontSize: "1rem" }}>
          Remove {pendingDelete?.name}?
        </DialogTitle>
        <DialogContent>
          <p style={{ margin: 0, fontSize: "0.86rem", lineHeight: 1.55, color: colors.textMed }}>
            This removes them from the team structure only. Their account, leads
            and history are not affected.
            {pendingDelete?.teamSize > 0 ? (
              <>
                {" "}
                <strong style={{ color: colors.veryHot }}>
                  Their {pendingDelete.teamSize}{" "}
                  {pendingDelete.teamSize === 1 ? "report" : "reports"} will lose
                  their place in the tree.
                </strong>
              </>
            ) : null}
          </p>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button
            onClick={() => setPendingDelete(null)}
            sx={{ textTransform: "none", fontWeight: 600, color: colors.textMed }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDelete}
            disabled={isDeleting}
            variant="contained"
            sx={{
              textTransform: "none", fontWeight: 600, borderRadius: "8px",
              backgroundColor: colors.veryHot,
              "&:hover": { backgroundColor: "#c00" },
            }}
          >
            {isDeleting ? "Removing…" : "Remove"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

const HeadRight = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const TextAction = styled.button`
  font-family: inherit;
  font-size: 0.76rem;
  font-weight: 600;
  padding: 6px 8px;
  border: none;
  border-radius: 6px;
  background: transparent;
  color: ${colors.brown};
  cursor: pointer;
  white-space: nowrap;

  &:hover { background: ${colors.lightWhite}; }
  &:focus-visible { outline: 2px solid ${colors.textBlue}; outline-offset: 1px; }
`;

const Segmented = styled.div`
  display: inline-flex;
  padding: 2px;
  border-radius: 8px;
  background: ${colors.inputWhite};
`;

const Seg = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-family: inherit;
  font-size: 0.76rem;
  font-weight: 600;
  padding: 6px 11px;
  min-height: 32px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  transition: background 160ms ease, color 160ms ease;
  background: ${(p) => (p.$active ? colors.white : "transparent")};
  color: ${(p) => (p.$active ? colors.brown : colors.textMed)};
  box-shadow: ${(p) => (p.$active ? "0 1px 3px rgba(0,0,0,0.12)" : "none")};

  svg { font-size: 1rem; }
  &:focus-visible { outline: 2px solid ${colors.textBlue}; outline-offset: 1px; }
`;

const Loading = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 240px;
`;

const ChartArea = styled.div`
  position: relative;
  width: 100%;
  height: 62vh;
  min-height: 340px;
  max-height: 720px;
  overflow: hidden;

  @media (max-width: 720px) {
    height: 52vh;
    min-height: 280px;
  }
`;

/** Horizontal scroll lives here, so the page itself never scrolls sideways. */
const TableScroll = styled.div`
  width: 100%;
  overflow-x: auto;
  max-height: 66vh;
  overflow-y: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.82rem;

  thead th {
    position: sticky;
    top: 0;
    z-index: 1;
    text-align: left;
    font-size: 0.66rem;
    font-weight: 700;
    letter-spacing: 0.6px;
    text-transform: uppercase;
    color: ${colors.textLight};
    background: ${colors.lightWhite};
    padding: 9px 14px;
    border-bottom: 1px solid ${colors.inputBorderWhite};
    white-space: nowrap;
  }

  tbody td {
    padding: 0 14px;
    height: 42px;
    border-bottom: 1px solid ${colors.inputBorderWhite};
    vertical-align: middle;
  }

  tbody tr:hover td {
    background: ${colors.lightWhite};
  }

  .num { text-align: right; font-variant-numeric: tabular-nums; }
  .actions { width: 56px; text-align: right; }
  .mgr { color: ${colors.textMed}; }

  .sr {
    position: absolute;
    width: 1px; height: 1px;
    overflow: hidden;
    clip: rect(0 0 0 0);
    white-space: nowrap;
  }
`;

/**
 * Depth via a guide line rather than padding alone — four levels in, padding
 * on its own stops telling you who reports to whom.
 */
const NameCell = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  position: relative;
  padding-left: ${(p) => p.$level * 22}px;

  ${(p) =>
    p.$level > 0 &&
    `
    &::before {
      content: "";
      position: absolute;
      left: ${(p.$level - 1) * 22 + 10}px;
      top: -11px;
      bottom: -11px;
      width: 1px;
      background: ${colors.inputBorderWhite};
    }
  `}

  .who {
    font-weight: 600;
    color: ${colors.text};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const Toggle = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  flex-shrink: 0;
  padding: 0;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  background: transparent;
  color: ${colors.brown};

  &:hover { background: ${colors.inputWhite}; }
  &:focus-visible { outline: 2px solid ${colors.textBlue}; outline-offset: 1px; }
  svg { font-size: 1.15rem; }
`;

const Spacer = styled.span`
  width: 22px;
  flex-shrink: 0;
`;

/**
 * Initials rather than photos. At 42px rows, avatars were the tallest thing in
 * the row and broken ones leaked fragments of their alt text.
 */
const Initial = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  flex-shrink: 0;
  border-radius: 50%;
  font-size: 0.68rem;
  font-weight: 700;
  color: ${colors.brown};
  background: ${colors.inputWhite};
`;

const Role = styled.span`
  font-size: 0.64rem;
  font-weight: 700;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  color: ${colors.textMed};
  white-space: nowrap;
`;

const Dash = styled.span`
  color: ${colors.textLight};
`;

const RowAction = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  padding: 0;
  border: 1px solid transparent;
  border-radius: 7px;
  cursor: pointer;
  background: transparent;
  /* Muted until hover: a column of red icons reads as a column of warnings. */
  color: ${colors.textLight};
  transition: color 150ms ease, background 150ms ease, border-color 150ms ease;

  &:hover {
    color: ${colors.veryHot};
    background: rgba(232, 11, 11, 0.06);
    border-color: rgba(232, 11, 11, 0.18);
  }
  &:focus-visible { outline: 2px solid ${colors.textBlue}; outline-offset: 1px; }
  svg { font-size: 1.05rem; }
`;
